import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, Mail, Users, MousePointerClick, Eye, UserMinus, Link2, AlertTriangle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { broadcastCount, broadcastTest, broadcastSend, listBroadcasts, getAnalyticsDimensions } from "./adminApi";
import { API } from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function Broadcast() {
  const { slug } = useParams();
  const [dims, setDims] = useState({ platforms: [], triggers: [], content_pieces: [] });
  const [seg, setSeg] = useState({ platform: "", trigger: "", content_piece: "", keep_me_in: false });
  const [eligible, setEligible] = useState(null);
  const [msg, setMsg] = useState({ subject: "", body: "", cta_text: "", cta_url: "", content_piece: "", use_fanly_link: true });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);

  useEffect(() => { getAnalyticsDimensions(slug).then(setDims).catch(() => {}); }, [slug]);
  const loadBroadcasts = useCallback(() => {
    listBroadcasts(slug).then((b) => {
      setBroadcasts(b);
      setMsg((m) => (m.content_piece ? m : { ...m, content_piece: `email-drop${b.length + 1}` }));
    }).catch(() => {});
  }, [slug]);
  useEffect(() => { loadBroadcasts(); }, [loadBroadcasts]);

  const refreshCount = useCallback(async () => {
    try { const r = await broadcastCount(slug, seg); setEligible(r.eligible); }
    catch (e) { setEligible(null); }
  }, [slug, seg]);
  useEffect(() => { refreshCount(); }, [refreshCount]);

  const validMsg = msg.subject.trim() && msg.body.trim();

  const sendTest = async () => {
    if (!validMsg) { toast.error("Add a subject and body first"); return; }
    if (!testTo) { toast.error("Enter a test email"); return; }
    setBusy(true);
    try { await broadcastTest(slug, { ...msg, segment: seg, to: testTo }); toast.success(`Test sent to ${testTo}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); }
  };

  const doSend = async () => {
    setConfirmOpen(false); setBusy(true);
    try {
      const r = await broadcastSend(slug, { ...msg, segment: seg });
      toast.success(`Sent to ${r.delivered}/${r.sent} fans`);
      setMsg({ subject: "", body: "", cta_text: "", cta_url: "", content_piece: "", use_fanly_link: true });
      loadBroadcasts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="broadcast-screen" className="space-y-8">
      <div>
        <h2 className="font-display text-3xl font-bold tracking-tight">Broadcast</h2>
        <p className="text-[#8a8a8a] text-sm mt-1">Emails go only to fans with the explicit future-updates opt-in (the closing-block "Keep me in"). We never send to no-consent, opted-out or unsubscribed fans. Every email includes Manage preferences + Unsubscribe.</p>
      </div>

      {/* segment */}
      <div className="rounded-2xl border border-white/10 p-5 space-y-4">
        <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Audience segment</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <select data-testid="seg-platform" value={seg.platform} onChange={(e) => setSeg({ ...seg, platform: e.target.value })}
            className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
            <option value="">All platforms</option>
            {dims.platforms.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="seg-trigger" value={seg.trigger} onChange={(e) => setSeg({ ...seg, trigger: e.target.value })}
            className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
            <option value="">All triggers</option>
            {dims.triggers.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <select data-testid="seg-content" value={seg.content_piece} onChange={(e) => setSeg({ ...seg, content_piece: e.target.value })}
            className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
            <option value="">All content</option>
            {dims.content_pieces.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="flex items-center justify-between gap-2 bg-[#0e0e0e] border border-white/15 rounded-lg px-3 py-2">
            <span className="font-mono-x text-[11px] text-[#a3a3a3]">Keep Me In</span>
            <Switch data-testid="seg-kmi" checked={seg.keep_me_in} onCheckedChange={(v) => setSeg({ ...seg, keep_me_in: v })} />
          </label>
        </div>
        <p className="flex items-center gap-2 text-sm" data-testid="eligible-count">
          <Users className="w-4 h-4 text-[#FF2A2A]" />
          {eligible === null ? "…" : <><span className="font-display text-xl font-bold">{eligible}</span> eligible fans will receive this email</>}
        </p>
      </div>

      {/* editor */}
      <div className="rounded-2xl border border-white/10 p-5 space-y-4">
        <div>
          <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Subject</label>
          <input data-testid="bc-subject" value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 focus:border-[#FF2A2A] outline-none py-2 text-lg text-white" />
        </div>
        <div>
          <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Body</label>
          <textarea data-testid="bc-body" rows={6} value={msg.body} onChange={(e) => setMsg({ ...msg, body: e.target.value })}
            className="w-full bg-transparent border border-white/15 rounded-lg focus:border-[#FF2A2A] outline-none p-3 text-[#e5e5e5] resize-y" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">CTA text</label>
            <input data-testid="bc-cta-text" value={msg.cta_text} onChange={(e) => setMsg({ ...msg, cta_text: e.target.value })} placeholder="Listen now"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#FF2A2A] outline-none py-2 text-white" />
          </div>
          <div>
            <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Content piece (this drop's tag)</label>
            <input data-testid="bc-content-piece" value={msg.content_piece} onChange={(e) => setMsg({ ...msg, content_piece: e.target.value })} placeholder="email-drop1"
              className="w-full bg-transparent border-b border-white/20 focus:border-[#FF2A2A] outline-none py-2 text-white" />
          </div>
        </div>

        {/* CTA link mode */}
        <div className="rounded-xl border border-white/10 p-4 space-y-3">
          <label className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-sm"><Link2 className="w-4 h-4 text-[#FF2A2A]" /> Use auto FANLY Link (recommended — full tracking)</span>
            <Switch data-testid="bc-use-fanly" checked={msg.use_fanly_link} onCheckedChange={(v) => setMsg({ ...msg, use_fanly_link: v })} />
          </label>
          {msg.use_fanly_link ? (
            <p data-testid="bc-fanly-link-preview" className="font-mono-x text-[11px] text-[#8a8a8a] break-all bg-black/40 rounded-lg px-3 py-2">
              {`${API}/share/${slug}?src=email&content=${encodeURIComponent(msg.content_piece || "email-drop")}&broadcast=<auto>`}
            </p>
          ) : (
            <>
              <input data-testid="bc-cta-url" value={msg.cta_url} onChange={(e) => setMsg({ ...msg, cta_url: e.target.value })} placeholder="https://your-external-link.com"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#FF2A2A] outline-none py-2 text-white" />
              <p data-testid="bc-external-warning" className="flex items-start gap-2 text-[12px] text-[#FFB020]">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                External URL: clicks are tracked, but FANLY loses the Visit → Listen → Complete → Return attribution for this link.
              </p>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-white/10">
          <input data-testid="bc-test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test@email.com"
            className="bg-[#0e0e0e] border border-white/15 rounded-lg px-3 py-2 text-sm text-white flex-1 min-w-[180px]" />
          <button data-testid="bc-send-test" onClick={sendTest} disabled={busy}
            className="flex items-center gap-2 border border-white/20 rounded-full px-5 py-2.5 text-sm hover:border-[#FF2A2A] transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send test
          </button>
          <button data-testid="bc-send" onClick={() => { if (!validMsg) { toast.error("Add a subject and body first"); return; } if (!eligible) { toast.error("No eligible fans for this segment"); return; } setConfirmOpen(true); }}
            disabled={busy} className="flex items-center gap-2 bg-[#FF2A2A] text-white font-semibold rounded-full px-6 py-2.5 text-sm hover:scale-[1.02] transition-transform disabled:opacity-60">
            <Send className="w-4 h-4" /> Send broadcast
          </button>
        </div>
      </div>

      {/* history */}
      <div>
        <h3 className="font-display text-lg font-bold tracking-tight mb-3">Past broadcasts</h3>
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-sm" data-testid="broadcasts-table">
            <thead>
              <tr className="text-left font-mono-x text-[10px] tracking-widest uppercase text-[#737373] border-b border-white/10">
                <th className="px-4 py-3">Subject</th><th className="px-4 py-3">Sent</th>
                <th className="px-4 py-3"><Eye className="inline w-3.5 h-3.5" /> Opens</th>
                <th className="px-4 py-3"><MousePointerClick className="inline w-3.5 h-3.5" /> Clicks</th>
                <th className="px-4 py-3">Visits</th><th className="px-4 py-3">Listens</th>
                <th className="px-4 py-3">Complete</th><th className="px-4 py-3">Return</th>
                <th className="px-4 py-3"><UserMinus className="inline w-3.5 h-3.5" /> Unsub</th>
                <th className="px-4 py-3">When</th>
              </tr>
            </thead>
            <tbody>
              {broadcasts.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-6 text-[#737373]">No broadcasts sent yet.</td></tr>
              ) : broadcasts.map((b) => (
                <tr key={b.id} className="border-b border-white/5" data-testid={`broadcast-row-${b.id}`}>
                  <td className="px-4 py-3 font-medium max-w-[220px] truncate">{b.subject}</td>
                  <td className="px-4 py-3">{b.delivered}/{b.sent}</td>
                  <td className="px-4 py-3">{b.unique_opens} <span className="text-[#666]">({b.opens})</span></td>
                  <td className="px-4 py-3 text-[#FF2A2A] font-semibold">{b.unique_clicks} <span className="text-[#666] font-normal">({b.clicks})</span></td>
                  <td className="px-4 py-3">{b.fanly_visits ?? 0}</td>
                  <td className="px-4 py-3">{b.listens ?? 0}</td>
                  <td className="px-4 py-3">{b.complete_listens ?? 0}</td>
                  <td className="px-4 py-3">{b.returning_fans ?? 0}</td>
                  <td className="px-4 py-3">{b.unsubscribed_count}</td>
                  <td className="px-4 py-3 text-[#8a8a8a]">{fmtDate(b.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-mono-x text-[10px] text-[#555] mt-2">Opens are indicative (email clients auto-load images). Clicks are the real engagement signal.</p>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-[#0e0e0e] border-white/10 text-white" data-testid="bc-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Send this broadcast?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#a3a3a3]">
              <span className="text-white font-semibold">{eligible} eligible fans</span> will receive “{msg.subject}”. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="bc-confirm-send" onClick={doSend} className="bg-[#FF2A2A] text-white hover:bg-[#e01f1f]">Send now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
